import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface SlideImage {
  id: string
  name: string
  file_path: string
  thumbnail_url?: string
  upload_date: string
}

interface CytologyReport {
  id: number
  patient: number
  patient_name: string
  patient_code: string
  report_date: string
}

const CytologyReportImageGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [report, setReport] = useState<CytologyReport | null>(null)
  const [patientImages, setPatientImages] = useState<SlideImage[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (id) {
      fetchReportAndImages()
    }
  }, [id])

  const fetchReportAndImages = async () => {
    try {
      setIsLoading(true)

      // Fetch report info
      const reportResponse = await fetch(`/api/v1/pathology/cytology-reports/${id}/`)
      if (!reportResponse.ok) {
        toast.error('Không thể tải thông tin phiếu kết quả')
        navigate('/pathology/reports')
        return
      }
      const reportData = await reportResponse.json()
      setReport(reportData)

      // Fetch patient's active case
      if (reportData.patient) {
        const casesResponse = await fetch(`/api/v1/pathology/cases/?patient=${reportData.patient}`)
        if (casesResponse.ok) {
          const casesData = await casesResponse.json()
          const cases = casesData.results || casesData || []

          logger.debug('📋 Cases found:', cases.length, cases)

          // Get the first (active) case
          if (cases.length > 0) {
            const activeCase = cases[0]
            const slidesUrl = `/api/v1/imaging/slides/?case=${activeCase.id}`

            logger.debug('🔍 Fetching slides from:', slidesUrl)

            // Fetch slides for this case
            const imagesResponse = await fetch(slidesUrl)
            if (imagesResponse.ok) {
              const imagesData = await imagesResponse.json()
              const slides = imagesData.results || imagesData || []

              logger.debug('🖼️ Slides received:', slides.length, 'for case', activeCase.id)
              logger.debug('📊 API response:', imagesData)

              setPatientImages(slides)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Lỗi khi tải dữ liệu')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Đang tải hình ảnh...</p>
        </div>
      </div>
    )
  }

  if (!report) {
    return null
  }

  if (patientImages.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-6xl mb-4">📭</div>
          <h2 className="text-2xl font-bold mb-2">Không có hình ảnh</h2>
          <p className="text-gray-400 mb-6">
            Bệnh nhân <span className="text-white font-medium">{report.patient_name}</span> chưa có hình ảnh nào
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Quay lại trang chủ
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">🔬 Hình ảnh tế bào học</h1>
              <p className="text-gray-400 mt-1">
                Bệnh nhân: <span className="text-white font-medium">{report.patient_name}</span>
                {' '}• Mã BN: <span className="text-white font-medium">{report.patient_code}</span>
                {' '}• Ngày: {new Date(report.report_date).toLocaleDateString('vi-VN')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Tổng số hình ảnh</p>
              <p className="text-3xl font-bold text-blue-400">{patientImages.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Image Grid */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {patientImages.map((img, index) => (
            <div
              key={img.id}
              className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-blue-500 transition-all hover:shadow-lg hover:shadow-blue-500/50"
            >
              <a
                href={`/imaging/viewer/${img.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                {/* Image Preview */}
                <div className="relative bg-gray-900 aspect-square flex items-center justify-center">
                  {img.thumbnail_url ? (
                    <img
                      src={img.thumbnail_url}
                      alt={img.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-8xl">🔬</div>
                  )}
                  <div className="absolute top-2 left-2 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    #{index + 1}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 truncate">{img.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">
                      {new Date(img.upload_date).toLocaleDateString('vi-VN')}
                    </span>
                    <span className="text-blue-400 text-sm font-medium flex items-center">
                      Xem chi tiết
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 border-t border-gray-700 p-4 mt-8">
        <div className="max-w-7xl mx-auto text-center text-gray-400 text-sm">
          <p>💡 Click vào bất kỳ hình ảnh nào để xem chi tiết</p>
        </div>
      </div>
    </div>
  )
}

export default CytologyReportImageGallery
